import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  adminKeys,
  fetchAdminImpersonations,
  fetchAdminTenant,
  fetchAdminTenantAudit,
  fetchAdminTenants,
  revokeImpersonation,
  startImpersonation,
} from '../../api/admin';
import { useMe } from '../auth/useMe';
import { ErrorNotice } from '../../components/ErrorNotice';
import { LoadingScreen } from '../../components/LoadingScreen';
import type {
  AdminAuditEntry,
  AdminImpersonationGrant,
  AdminTenantSummary,
} from '../../../src/shared/admin';

/**
 * `/admin` — platform-admin console (ARCHITECTURE_PLAN §10). Rendered only
 * for callers passing `RequirePlatformAdmin` (route guard). The console:
 *   - lists tenants (keyset-paginated);
 *   - drills into a tenant's detail + audit history;
 *   - lists active + past impersonation grants;
 *   - starts an impersonation session (reason required, §6.4) — the server
 *     replaces the session cookie and the SPA invalidates `GET /api/auth/me`
 *     so the banner renders and the admin is routed into the tenant's `/w/:slug`;
 *   - revokes an active grant immediately.
 *
 * Every failure renders the safe message + requestId (no blank states, §10).
 */
export function AdminPage(): React.JSX.Element {
  const me = useMe();
  const queryClient = useQueryClient();

  const tenantsQuery = useQuery({
    queryKey: adminKeys.tenants,
    queryFn: ({ signal }) => fetchAdminTenants(undefined, signal),
  });

  const impersonationsQuery = useQuery({
    queryKey: adminKeys.impersonations,
    queryFn: ({ signal }) => fetchAdminImpersonations(undefined, signal),
  });

  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);

  const startMutation = useMutation({
    mutationFn: startImpersonation,
    onSuccess: () => {
      // The server replaced the session cookie with an impersonation session.
      // Drop all cached server state and re-bootstrap so the banner renders.
      queryClient.clear();
      void queryClient.invalidateQueries({ queryKey: adminKeys.impersonations });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (grantId: string) => revokeImpersonation(grantId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.impersonations });
    },
  });

  if (me.isPending || me.data === undefined) {
    return <LoadingScreen />;
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-6 py-3">
          <div className="flex items-center gap-6">
            <span className="text-sm font-semibold tracking-tight">Astralis FrontDesk · Admin</span>
            <Link to="/" className="text-sm font-medium text-slate-500 hover:text-slate-900">
              Home
            </Link>
          </div>
          <span className="text-sm text-slate-600">{me.data.user?.email}</span>
        </div>
      </header>

      <main className="mx-auto max-w-5xl space-y-8 px-6 py-8">
        <section>
          <div className="flex items-center justify-between gap-4">
            <h1 className="text-2xl font-semibold tracking-tight">Tenants</h1>
            <Link
              to="/admin/tenants/new"
              className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
            >
              New tenant
            </Link>
          </div>
          <p className="mt-1 text-sm text-slate-500">
            Platform admin console — tenant list, audit, and impersonation (§6.4).
          </p>

          {tenantsQuery.isPending && <LoadingScreen />}
          {tenantsQuery.isError && <ErrorNotice error={tenantsQuery.error} />}
          {tenantsQuery.isSuccess && (
            <TenantsList
              tenants={tenantsQuery.data.tenants}
              nextCursor={tenantsQuery.data.nextCursor}
              onSelect={(id) => setSelectedTenantId(id)}
              selectedId={selectedTenantId}
              onStartImpersonation={(tenant) =>
                startMutation.mutate({ tenantId: tenant.id, reason: 'Manual support review' })
              }
              startingTenantId={
                startMutation.isPending && startMutation.variables
                  ? startMutation.variables.tenantId
                  : null
              }
            />
          )}
          {startMutation.isError && (
            <div className="mt-3">
              <ErrorNotice error={startMutation.error} />
            </div>
          )}
        </section>

        {selectedTenantId !== null && (
          <section>
            <TenantDetailPanel tenantId={selectedTenantId} />
          </section>
        )}

        <section>
          <h2 className="text-xl font-semibold tracking-tight">Impersonation grants</h2>
          <p className="mt-1 text-sm text-slate-500">
            Grants are TTL-bounded (≤ 60 min), audited, and revocable immediately. An active grant
            scopes a platform admin into one tenant with admin capabilities MINUS billing (§6.4).
          </p>
          {impersonationsQuery.isPending && <LoadingScreen />}
          {impersonationsQuery.isError && <ErrorNotice error={impersonationsQuery.error} />}
          {impersonationsQuery.isSuccess && (
            <GrantsList
              grants={impersonationsQuery.data.grants}
              nextCursor={impersonationsQuery.data.nextCursor}
              revokingId={
                revokeMutation.isPending && revokeMutation.variables
                  ? revokeMutation.variables
                  : null
              }
              onRevoke={(grantId) => revokeMutation.mutate(grantId)}
              revokeError={revokeMutation.isError ? revokeMutation.error : null}
            />
          )}
        </section>
      </main>
    </div>
  );
}

function TenantsList(props: {
  tenants: AdminTenantSummary[];
  nextCursor: string | null;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onStartImpersonation: (tenant: AdminTenantSummary) => void;
  startingTenantId: string | null;
}): React.JSX.Element {
  if (props.tenants.length === 0) {
    return (
      <div className="mt-4 rounded-lg border border-slate-200 bg-white p-8 text-center">
        <p className="text-sm text-slate-500">No tenants provisioned yet.</p>
      </div>
    );
  }
  return (
    <ul className="mt-4 space-y-2" data-testid="admin-tenants-list">
      {props.tenants.map((tenant) => (
        <li
          key={tenant.id}
          className={`rounded-lg border bg-white p-4 shadow-sm ${
            props.selectedId === tenant.id ? 'border-slate-900' : 'border-slate-200'
          }`}
        >
          <div className="flex items-start justify-between gap-4">
            <button
              type="button"
              onClick={() => props.onSelect(tenant.id)}
              className="min-w-0 flex-1 text-left"
              data-testid={`tenant-row-${tenant.id}`}
            >
              <div className="flex items-center gap-2">
                <span className="truncate text-sm font-medium text-slate-900">{tenant.name}</span>
                <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-700">
                  {tenant.plan}
                </span>
                <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-700">
                  {tenant.status}
                </span>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                <code className="font-mono">{tenant.slug}</code> · {tenant.vertical} ·{' '}
                {tenant.timezone}
              </p>
            </button>
            <button
              type="button"
              onClick={() => props.onStartImpersonation(tenant)}
              disabled={props.startingTenantId === tenant.id}
              className="shrink-0 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
              data-testid={`impersonate-btn-${tenant.id}`}
            >
              {props.startingTenantId === tenant.id ? 'Starting…' : 'Impersonate'}
            </button>
          </div>
        </li>
      ))}
    </ul>
  );
}

function TenantDetailPanel(props: { tenantId: string }): React.JSX.Element {
  const tenantQuery = useQuery({
    queryKey: adminKeys.tenant(props.tenantId),
    queryFn: ({ signal }) => fetchAdminTenant(props.tenantId, signal),
  });
  const auditQuery = useQuery({
    queryKey: adminKeys.tenantAudit(props.tenantId),
    queryFn: ({ signal }) => fetchAdminTenantAudit(props.tenantId, undefined, signal),
  });

  return (
    <div
      className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
      data-testid="tenant-detail-panel"
    >
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-xl font-semibold tracking-tight">Tenant detail</h2>
        <div className="flex items-center gap-4">
          <Link
            to={`/admin/tenants/${props.tenantId}/numbers`}
            className="text-sm font-medium text-slate-600 underline underline-offset-4 hover:text-slate-900"
          >
            Manage numbers
          </Link>
          <Link
            to={`/admin/tenants/${props.tenantId}/secrets`}
            className="text-sm font-medium text-slate-600 underline underline-offset-4 hover:text-slate-900"
          >
            Manage secrets
          </Link>
        </div>
      </div>
      {tenantQuery.isPending && <LoadingScreen />}
      {tenantQuery.isError && <ErrorNotice error={tenantQuery.error} className="mt-3" />}
      {tenantQuery.isSuccess && (
        <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="text-slate-500">Name</dt>
          <dd>{tenantQuery.data.tenant.name}</dd>
          <dt className="text-slate-500">Slug</dt>
          <dd>
            <code className="font-mono text-xs">{tenantQuery.data.tenant.slug}</code>
          </dd>
          <dt className="text-slate-500">Plan / status</dt>
          <dd>
            {tenantQuery.data.tenant.plan} / {tenantQuery.data.tenant.status}
          </dd>
          <dt className="text-slate-500">Timezone</dt>
          <dd>{tenantQuery.data.tenant.timezone}</dd>
        </dl>
      )}

      <h3 className="mt-6 text-sm font-medium text-slate-700">Audit log</h3>
      {auditQuery.isPending && <LoadingScreen />}
      {auditQuery.isError && <ErrorNotice error={auditQuery.error} className="mt-2" />}
      {auditQuery.isSuccess && <AuditList entries={auditQuery.data.entries} />}
    </div>
  );
}

function AuditList(props: { entries: AdminAuditEntry[] }): React.JSX.Element {
  if (props.entries.length === 0) {
    return (
      <p className="mt-2 text-sm text-slate-500" data-testid="audit-empty">
        No audit entries yet.
      </p>
    );
  }
  return (
    <ul className="mt-2 space-y-1" data-testid="audit-list">
      {props.entries.map((entry) => (
        <li
          key={entry.id}
          className="rounded border border-slate-100 bg-slate-50 px-3 py-2 text-xs"
          data-testid={`audit-row-${entry.id}`}
        >
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                entry.actorType === 'impersonation'
                  ? 'bg-amber-100 text-amber-800'
                  : entry.actorType === 'platform_admin'
                    ? 'bg-purple-100 text-purple-800'
                    : 'bg-slate-100 text-slate-700'
              }`}
            >
              {entry.actorType}
            </span>
            <span className="font-medium text-slate-900">{entry.action}</span>
            <span className="text-slate-500">{new Date(entry.createdAt).toISOString()}</span>
          </div>
          <p className="mt-0.5 text-slate-600">
            <span className="font-mono">
              {entry.targetType}:{entry.targetId}
            </span>
            {entry.impersonationGrantId !== null && (
              <>
                {' '}
                · grant <code className="font-mono">{entry.impersonationGrantId}</code>
              </>
            )}
          </p>
        </li>
      ))}
    </ul>
  );
}

function GrantsList(props: {
  grants: AdminImpersonationGrant[];
  nextCursor: string | null;
  revokingId: string | null;
  onRevoke: (grantId: string) => void;
  revokeError: unknown;
}): React.JSX.Element {
  if (props.grants.length === 0) {
    return (
      <p className="mt-4 text-sm text-slate-500" data-testid="grants-empty">
        No impersonation grants have ever been issued.
      </p>
    );
  }
  return (
    <div className="mt-4">
      {props.revokeError !== null && (
        <div className="mb-3">
          <ErrorNotice error={props.revokeError} />
        </div>
      )}
      <ul className="space-y-2" data-testid="grants-list">
        {props.grants.map((grant) => (
          <li
            key={grant.id}
            className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
            data-testid={`grant-row-${grant.id}`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                      grant.active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    {grant.active ? 'active' : grant.revokedAt !== null ? 'revoked' : 'expired'}
                  </span>
                  <span className="truncate text-sm font-medium text-slate-900">
                    tenant <code className="font-mono text-xs">{grant.tenantId}</code>
                  </span>
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  Reason: {grant.reason} · expires {new Date(grant.expiresAt).toISOString()} ·
                  created {new Date(grant.createdAt).toISOString()}
                </p>
              </div>
              {grant.active && (
                <button
                  type="button"
                  onClick={() => props.onRevoke(grant.id)}
                  disabled={props.revokingId === grant.id}
                  className="shrink-0 rounded-md border border-red-200 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                  data-testid={`revoke-btn-${grant.id}`}
                >
                  {props.revokingId === grant.id ? 'Revoking…' : 'Revoke'}
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
